import { Check, Pencil, Plus, Rocket, Send, X } from "lucide-react";
import { useState, type FC, type ReactNode } from "react";

import { BrandMark } from "@/components/brand";
import { Surface, SurfaceHeader } from "@/components/surface";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type StepKind = "done" | "active" | "next";

const StepMarker: FC<{ kind: StepKind; number: number }> = ({
  kind,
  number
}) => (
  <span
    aria-hidden
    className={cn(
      "flex size-5 shrink-0 items-center justify-center rounded-sm text-xs font-medium tabular-nums",
      kind === "done" && "bg-primary text-primary-foreground",
      kind === "active" && "bg-accent-soft text-primary",
      kind === "next" && "bg-fill-2 text-fg-3"
    )}
  >
    {kind === "done" ? <Check className="size-3" /> : number}
  </span>
);

const Step: FC<{
  number: number;
  kind: StepKind;
  title: string;
  description?: string;
  isLast?: boolean;
  children?: ReactNode;
}> = ({ number, kind, title, description, isLast = false, children }) => (
  <li className="relative flex gap-3">
    <div className="flex flex-col items-center">
      <StepMarker kind={kind} number={number} />
      {!isLast ? (
        <span className="my-1 w-px flex-1 bg-line-2" aria-hidden />
      ) : null}
    </div>
    <div
      className={cn("flex min-w-0 flex-1 flex-col gap-1", !isLast && "pb-5")}
    >
      <span
        className={cn(
          "text-base leading-5 font-medium",
          kind === "active" ? "text-foreground" : "text-muted-foreground"
        )}
      >
        {title}
      </span>
      {description ? (
        <p className="text-xs text-muted-foreground">{description}</p>
      ) : null}
      {children ? (
        <div className="mt-2 flex flex-wrap items-center gap-2">{children}</div>
      ) : null}
    </div>
  </li>
);

export type GetStartedCardProps = {
  clientFullName: string;
  clientFirstName: string;
  casesCount: number;
  intakesCount: number;
  /** Visa label of the newest case, if any. */
  visaTypeLabel: string | null;
  onStartCase: () => void;
  onEditCase: () => void;
  onNewRequest: () => void;
};

/**
 * Onboarding checklist for a brand-new client (no requests yet, at most one
 * case): profile created -> create a case -> send the first request.
 */
export const GetStartedCard: FC<GetStartedCardProps> = ({
  clientFullName,
  clientFirstName,
  casesCount,
  intakesCount,
  visaTypeLabel,
  onStartCase,
  onEditCase,
  onNewRequest
}) => {
  const [showWelcome, setShowWelcome] = useState(true);
  const safeName = (clientFirstName || clientFullName || "this client").trim();
  const hasCase = casesCount >= 1;
  const hasSentRequest = intakesCount >= 1;
  const visaLabel = (visaTypeLabel ?? "").trim();

  const caseKind: StepKind = hasCase ? "done" : "active";
  const requestKind: StepKind = !hasCase
    ? "next"
    : hasSentRequest
    ? "done"
    : "active";

  return (
    <Surface data-testid="get-started-card">
      <SurfaceHeader icon={Rocket} title="Get Started" />
      <div className="flex flex-col gap-5 px-4 py-4">
        {showWelcome ? (
          <div
            role="status"
            className="flex items-start gap-2.5 rounded-md bg-accent-soft p-3"
          >
            <BrandMark className="mt-0.5 size-5 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-base leading-5 font-medium">
                {`Welcome! Let's get ${safeName}'s case started.`}
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Follow the steps below to set up this client&apos;s profile,
                collect their information, and begin case preparation.
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label="Dismiss welcome message"
              onClick={() => setShowWelcome(false)}
            >
              <X />
            </Button>
          </div>
        ) : null}

        <ol className="flex flex-col">
          <Step number={1} kind="done" title="Create Client Profile" />

          <Step
            number={2}
            kind={caseKind}
            title="Create a Case"
            description={
              hasCase
                ? "Great — the case is set up. Next, send your client a new request."
                : `Select a visa type — H-1B, PERM, green card, or another immigration category — to begin case preparation for ${safeName}. Requests and templates will be tailored to the case.`
            }
          >
            {caseKind === "active" ? (
              <Button variant="primary" size="sm" onClick={onStartCase}>
                <Plus /> New Case
              </Button>
            ) : visaLabel ? (
              <Button
                variant="outline"
                size="sm"
                onClick={onEditCase}
                aria-label={`Change case type: ${visaLabel}`}
              >
                {visaLabel} <Pencil />
              </Button>
            ) : null}
          </Step>

          <Step
            number={3}
            kind={requestKind}
            isLast
            title="Request Documents & Information"
            description={
              requestKind === "next"
                ? "Create a case first so templates and document checklists match the visa type."
                : requestKind === "active"
                ? "Create a new request for this case. If there's a template, we'll prefill it with the relevant documents and/or information requests (don't worry, you can still customize this)."
                : "Your client can upload documents and complete their details whenever they're ready. Send more requests anytime from the Intake Requests tab."
            }
          >
            {requestKind === "active" ? (
              <Button variant="primary" size="sm" onClick={onNewRequest}>
                <Send />{" "}
                {visaLabel ? `New Request for ${visaLabel}` : "New Request"}
              </Button>
            ) : requestKind === "done" ? (
              <span className="text-xs text-muted-foreground">
                {intakesCount} request{intakesCount === 1 ? "" : "s"} sent
              </span>
            ) : null}
          </Step>
        </ol>
      </div>
    </Surface>
  );
};
